// The telemetry feed: what the tuner knows, delivered live to a dashboard.
//
// The search runs on a *different service* from the game — its own branch, its
// own `railway.tuner.json`, and above all its own volume, because a Railway
// volume is single-attach and the thing that owns `.tuning/` is the thing that
// writes it. So the page cannot read the files: it is served by a process that
// has never seen them.
//
// This is the seam. The tuner builds a bounded **digest** and publishes it to
// the game server; the server holds the latest one and fans it out to whoever
// is watching. Two roles on one socket, authed differently on purpose — a
// publisher carries a shared secret because it is writing, a subscriber comes
// through the site gate because it is only reading.
//
// **A digest and not a stream of events**, which is what makes the whole thing
// cheap to reason about: there is no ordering to preserve, no replay to catch
// up on, and a subscriber that connects halfway through is not behind. The
// last one wins, always. That is the opposite of the game socket next door,
// where the log *is* the state and every command matters — and the difference
// is that this is a measurement of something that already happened.

/** One board, drawn the way `tools/lib/render.mjs` draws it. */
export interface TunerFrame {
  ply: number;
  /** Rows of text, top rank first. */
  board: string[];
  /**
   * The state the drawing was made from, gzipped and base64'd.
   *
   * **Present on one game per army, not on all of them.** A drawing is what a
   * card shows and it is lossy — no direction on a loaded lane, any heap over
   * nine a star, and nothing anybody is carrying in it at all — so the
   * snapshot is what makes a board *openable* and countable. Shipping every
   * one would take the digest from ~140KB to most of a megabyte every thirty
   * seconds, to every browser with the page open; shipping the newest win per
   * profile is what the thing is actually for, which is a person checking that
   * each army behaves the way its style says.
   *
   * They are all on the volume regardless. This is only what crosses the wire.
   */
  state?: string;
}

/**
 * A game worth keeping a picture of.
 *
 * Wins are what the gallery is for — "this is what winning looks like" is the
 * question a fitness number cannot answer — and a sample of losses rides along
 * because a winning factory only means something beside one that lost.
 */
export interface TunerGame {
  /** Stable across republishes: the same game must not arrive twice. */
  id: string;
  at: number;
  profile: string;
  island: string;
  generation: number;
  /** The candidate's fitness in the generation this came from. */
  score: number;
  /** The opening seed, so the game can be replayed exactly. */
  seed: number;
  /** `classic`, or the scenario the batch rotated onto. */
  board: string;
  plies: number;
  /**
   * How long the simulation took, in milliseconds, or null.
   *
   * Wall clock on whichever machine played it — a pooled batch runs on the
   * runners, so this is the fleet's speed rather than the orchestrator's.
   * **Absent or null on any record written before it existed**, which is the
   * ordinary case for a gallery file on a box that outlives several deploys.
   */
  ms?: number | null;
  /** `checkmate`, `stalemate`, `playing` at the ply cap, … */
  status: string;
  /**
   * Which side finished it.
   *
   * `candidate` is the vector under search, `stock` whoever it was measured
   * against — the two teams every tuner game is between. A draw is neither,
   * and is kept because a draw at the ply cap is a result about the candidate
   * too.
   */
  outcome: "candidate" | "stock" | "draw";
  /**
   * Which army was sitting opposite, when it was not simply the defaults.
   *
   * A batch rotates over a panel of the field's own champions
   * (`tools/lib/panel.mjs`), so `stock` is a role rather than a name — and a
   * card that says "stock won" when another champion won is labelled with
   * the wrong army. Null on a game played against one named reference, which is
   * what the royale and the tournament play.
   */
  opponent?: string | null;
  /** That team's name, ready to print: the profile, or whoever beat it. */
  team: string;
  /** Which seat the winner held, or null for a draw. */
  side: 0 | 1 | null;
  /** Halfway through, where a factory either exists or does not. */
  mid: TunerFrame | null;
  /** How the argument ended. */
  final: TunerFrame | null;
  /** What each side had at the end, one line each. */
  tally: string[];
  /**
   * How many times this exact game was played.
   *
   * Every candidate in a generation plays the same openings, and early on most
   * of them play those openings identically — so the same picture arrives
   * several times over. They fold into one card carrying a count, which is
   * itself worth knowing: a high number means the generation has not diverged.
   */
  repeats: number;
}

/** One island's search, generation by generation. */
export interface TunerIsland {
  id: string;
  /**
   * When the island last wrote anything.
   *
   * An island is a *process*, and its directory outlives it by design — that is
   * what lets the next pass pick the champion up. So this is the only thing
   * separating "searching now" from "searched last Tuesday".
   */
  at: number;
  seed: number;
  /** What the seed vector scored before generation one. */
  baseScore: number;
  points: { generation: number; score: number }[];
}

/** A profile's search: where it is now, and how it got there. */
export interface TunerProfile {
  name: string;
  /** The current champion. */
  score: number;
  generation: number;
  island: string;
  /** When the champion last moved. */
  at: number;
  /**
   * Every champion this profile has offered, oldest first — the progress line.
   *
   * Off the append-only ledger rather than off the islands, because an island
   * is a population that dies with its process and the ledger is the only
   * thing that survives a pass.
   *
   * `basis` is what makes two of these comparable: a short hash of the goal,
   * the batch and the versions they were measured under. It changes far more
   * often than anybody expects — nine distinct values inside one shipped
   * profile's ledger — and the score visibly resets at each, so a line drawn
   * straight through them reports a collapse that never happened.
   */
  ledger: {
    at: number;
    score: number;
    /**
     * What the *opposition* scored on the same games, under the same goal.
     *
     * The control a score on its own does not have. A champion's number rising
     * can mean the search found something or it can mean the batch got easier,
     * and the basis hash only catches the causes somebody remembered to record
     * — a promote moves the panel's vectors, which it does catch, but the two
     * lines drawn together catch the rest by showing whether the gap widened.
     *
     * **Null means not recorded, never nought.** Every ledger line written
     * before this existed comes back without the field, and a zero would draw
     * as a real measurement of a level opponent.
     */
    foeScore: number | null;
    generation: number;
    island: string;
    basis: string;
    plannerVersion: number | null;
  }[];
  /**
   * The champion's weights — the artifact, as opposed to the telemetry.
   *
   * **This is what lets a tuned opponent reach players without a deploy.**
   * Everything else on this record is for a person to look at; these are for
   * the game to play with, and they ride here rather than on a channel of
   * their own because the digest already carries `plannerVersion` and
   * `metricsVersion`. Weights without those are a vector nobody can date, and
   * two pipes is two places for them to disagree.
   *
   * Null when the tuner has no champion for that profile yet.
   */
  weights?: Record<string, unknown> | null;
  /** The islands currently searching it. */
  islands: TunerIsland[];
  /**
   * Metrics from the champion's own batch: a fixed headline set every army
   * reports, plus everything this profile's goal actually scores.
   *
   * The second half is derived from the goal rather than listed, because a list
   * goes stale in silence and did — one goal's six fitness metrics were
   * measured, stored, and dropped one step from the screen.
   */
  metrics: Record<string, number>;
  /**
   * What `HEAD_LEAN` multiplied this champion's weighted sum by, or 1.
   *
   * **Computed on the tuner rather than here**, because `score()` multiplies
   * above nought and *divides* below it, and a client reimplementing that
   * branch is a second answer to how a score is made. Optional: every digest
   * written before it exists comes back without one, which reads as no lean.
   */
  lean?: number;
  /**
   * The coefficients `score` was computed with, or null where the committed
   * goals no longer name this profile.
   *
   * **A metric without its coefficient is a number with no scale**, which is
   * the complaint `profiles.json` makes twice about reading coefficients
   * without their metric, arriving from the other side: `bannedPerTurn` at
   * 0.004 says nothing until you know it is multiplied by -51,693.
   */
  goal?: Record<string, number> | null;
}

export interface TunerDigest {
  /** When the tuner built this. */
  at: number;
  /**
   * What the numbers were measured under.
   *
   * A score is a number from whatever basis it was taken on, so a dashboard
   * that showed two metrics versions on one axis would draw a step change and
   * call it progress. Displayed, never silently reconciled.
   */
  metricsVersion: number;
  plannerVersion: number;
  profiles: TunerProfile[];
  /**
   * How fast the fleet is simulating, over its most recent games.
   *
   * **Optional, and deliberately not in `isDigest`.** That guard is shallow on
   * purpose, and a digest mirrored to the volume before this existed — or
   * published by a tuner on an older commit — has to render rather than be
   * thrown out whole. Missing means the panel is omitted, never an empty one.
   */
  compute?: TunerCompute;
  gallery: TunerGame[];
}

/**
 * What a game costs to simulate, over the field's last few hundred.
 *
 * **The mean and the median both travel, because the mean alone lies here.** A
 * batch is mostly short games and occasionally one that runs to the ply cap on
 * a padded board, and one of those drags the mean somewhere no game actually
 * is. The median is what a person means by "how long does a game take"; the
 * mean is what throughput is made of.
 *
 * `msPerPly` is the figure that survives a change in how long games *run* — a
 * profile that starts winning faster plays shorter games without the simulator
 * being any quicker, which on duration alone would read as a speed-up.
 */
export interface TunerCompute {
  /** How many games the figures are over — may be short of `window`. */
  games: number;
  /** Mean milliseconds a game. */
  ms: number;
  /** Median milliseconds a game. */
  median: number;
  /** Pooled milliseconds a ply, so a long game weighs more than a short one. */
  msPerPly: number;
  /** Mean plies a game, which is the context `ms` needs. */
  plies: number;
  /** How many games were asked for. */
  window: number;
}

/**
 * One island, right now: how many games it has played and when it last played
 * one.
 *
 * The same record `tools/lib/health.mjs` writes per game, minus the fields only
 * a watchdog needs. `at` is when the *count last moved*, not when the file was
 * last written — the difference between "still working" and "still running",
 * which is the whole distinction this exists to draw.
 */
export interface TunerBeat {
  id: string;
  games: number;
  at: number;
  started: number;
  note?: string;
}

/**
 * The pulse: is the search alive, and what has it done in the last few seconds.
 *
 * **A second, much smaller payload on the same socket, and the split is the
 * point.** A digest is built from the whole of `.tuning/` and changes only when
 * a *generation* lands — an island that dispatches a batch and waits writes
 * nothing for minutes, so between boundaries the page showed a snapshot with no
 * sign of life and no way to tell a working search from a dead publisher. The
 * heartbeat that was supposed to answer that re-sent the entire digest, which
 * on the real box is ~300KB, to every open browser, once a minute, to prove
 * that something was breathing.
 *
 * So liveness moves to its own message read from its own source: the health
 * beats, which are a handful of small files written *per game*. Cheap enough to
 * send every few seconds, which is what makes a dead feed visible in seconds
 * rather than in minutes.
 *
 * **It is a fact about now**, so it is never mirrored to disk and never
 * restored: a pulse that survived a restart would be a claim that the search
 * was playing games at a moment nobody can vouch for.
 */
export interface TunerPulse {
  /** When the tuner read the beats. */
  at: number;
  /** Is a pass in flight? False makes a silent island the expected state. */
  expecting: boolean;
  /** When the pass started, or 0 between passes. */
  passStarted: number;
  /** Games played this pass, across every island. */
  games: number;
  islands: TunerBeat[];
}

/**
 * From the tuner, or from a browser.
 *
 * `publish` carries the secret on every message rather than at connect: the
 * connection is long-lived and a token checked once is a token that stays good
 * for as long as the socket does.
 */
export type TunerClientMessage =
  | { t: "publish"; token: string; digest: TunerDigest }
  | { t: "pulse"; token: string; pulse: TunerPulse }
  | { t: "watch" };

export type TunerServerMessage =
  /** The current digest, on connect and on every publish. Null before the first. */
  | { t: "digest"; digest: TunerDigest | null }
  /** The current pulse, on connect and every few seconds after. */
  | { t: "pulse"; pulse: TunerPulse | null }
  | { t: "ok" }
  | { t: "error"; reason: string };

export const TUNER_WS_PATH = "/tuner-ws";

/** Where a browser fetches the digest when it has no socket. */
export const TUNER_API_PATH = "/api/tuner";

/**
 * Where the *game* fetches the champions the tuner has found.
 *
 * Deliberately not `/api/tuner`: that one is the dashboard's and is gated on
 * the tuner password, and weights are gameplay data every player needs. One
 * authenticated publish, two reads — the notes for whoever unlocks them, the
 * artifact for everyone.
 */
export const CHAMPIONS_API_PATH = "/api/champions";

/**
 * The most a published digest may be.
 *
 * The builder bounds what goes in, so this is the backstop against a tuner
 * running different code — the publisher is authenticated but it is still a
 * process on another machine posting into memory this one has to hold.
 */
export const MAX_DIGEST_BYTES = 4 * 1024 * 1024;

/**
 * Same-origin socket URL, upgrading to wss: when the page is https:.
 *
 * Derived from the page for the same reason `socketUrl` is: one service, one
 * domain, and no build-time environment config to get wrong.
 */
export function tunerSocketUrl(location: { protocol: string; host: string }): string {
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}${TUNER_WS_PATH}`;
}

/**
 * Is this a digest?
 *
 * Asked on the way in, because a publisher is a *client* — authenticated, and
 * still not to be trusted with the shape of the thing every browser then
 * renders. Structural and shallow: the arrays are the part a page iterates, so
 * getting those wrong is what turns into a blank screen rather than a bad
 * number.
 */
export function isDigest(value: unknown): value is TunerDigest {
  if (typeof value !== "object" || value === null) return false;
  const digest = value as Partial<TunerDigest>;
  return (
    typeof digest.at === "number" &&
    typeof digest.metricsVersion === "number" &&
    typeof digest.plannerVersion === "number" &&
    Array.isArray(digest.profiles) &&
    Array.isArray(digest.gallery)
  );
}

/**
 * Is this a pulse?
 *
 * Same argument as `isDigest` and the same shallowness: `islands` is what the
 * page iterates, so an array that is not one is what turns into a blank strip
 * rather than a wrong number.
 */
export function isPulse(value: unknown): value is TunerPulse {
  if (typeof value !== "object" || value === null) return false;
  const pulse = value as Partial<TunerPulse>;
  return (
    typeof pulse.at === "number" &&
    typeof pulse.games === "number" &&
    typeof pulse.expecting === "boolean" &&
    Array.isArray(pulse.islands)
  );
}
